import classNames from 'classnames';
import { Parchment } from 'quill/core';
import type Quill from 'quill/core';
import React, { useEffect, useState } from 'react';
import Highlight from '../../../formats/Highlight';
import { highlightIcon, removeHighlightIcon } from '../../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './ToolbarButton';
import * as styles from './ToolbarHighlightButton.module.scss';

interface RemoveHighlightButtonProps {
  quill: Quill;
  hasCommonValue: boolean;
  onChange: (color: string | null) => void;
  onClose: () => void;
}

const RemoveHighlightButton = ({
  quill,
  hasCommonValue,
  onChange,
  onClose,
}: RemoveHighlightButtonProps) => {
  const [isShowing, setIsShowing] = useState(hasCommonValue);

  useEffect(() => {
    if (hasCommonValue) {
      setIsShowing(true);
    } else if (quill) {
      const { index, length } = quill.getSelection(true);
      const blots = quill.scroll.descendants(
        Parchment.InlineBlot,
        index,
        length,
      );
      setIsShowing(blots.some((blot) => !!blot.formats().highlight));
    } else {
      setIsShowing(false);
    }
  }, [quill, hasCommonValue]);

  return isShowing ? (
    <ToolbarButtonGroup>
      <ToolbarButton
        icon={removeHighlightIcon}
        onClick={() => {
          onChange(null);
          onClose();
        }}
        aria-label="Remove highlight"
      />
    </ToolbarButtonGroup>
  ) : null;
};

// @ts-expect-error Highlight.whitelist is never undefined
const colors: string[] = Highlight.whitelist;

interface ToolbarHighlightButtonProps {
  quill: Quill;
  disabled?: boolean;
  value: unknown;
  onChange: (color: string | null) => void;
}

const ToolbarHighlightButton = ({
  disabled,
  quill,
  value,
  onChange,
}: ToolbarHighlightButtonProps) => {
  return (
    <ToolbarButton
      disabled={disabled}
      icon={
        typeof value === 'string' ? (
          <div className={classNames(styles.highlightIcon, styles[value])} />
        ) : (
          highlightIcon
        )
      }
      tooltip="Highlight"
      renderPopOver={(onClose: () => void) => (
        <div className={styles.highlightMenu}>
          <ToolbarButtonGroup>
            {colors.map((color) => (
              <ToolbarButton
                key={color}
                onClick={() => {
                  onChange(color);
                  onClose();
                }}
                icon={
                  <div
                    className={classNames(styles.highlightIcon, styles[color], {
                      [styles.active]: color === value,
                    })}
                  />
                }
                aria-label={`Add ${color} highlight`}
              />
            ))}
          </ToolbarButtonGroup>
          <RemoveHighlightButton
            quill={quill}
            hasCommonValue={!!value}
            onChange={onChange}
            onClose={onClose}
          />
        </div>
      )}
    />
  );
};

export default ToolbarHighlightButton;
